import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '@/hooks/useModal';
import { useRecipientsStore } from '../stores';
import { fetchRecipientsList } from '@/lib/api/email-flows';
import { exportRecipientsToCSV } from '../utils';
import { RECIPIENTS_EXPORT_PAGE_SIZE } from '../constants';
import { QUERY_KEYS } from '@/lib/constants';
import type { Recipient } from '../types';

export const useRecipientsActions = () => {
    const queryClient = useQueryClient();
    const [emailsSent, setEmailsSent] = useState(0);
    const transformedFilters = useRecipientsStore(state => state.transformedFilters);

    const exportModal = useModal();
    const optInModal = useModal();
    const successModal = useModal();

    const handleExportCSV = useCallback(() => {
        exportModal.open();
    }, [exportModal]);

    const fetchPageData = useCallback(
        async (page: number): Promise<Recipient[]> => {
            const params = {
                page,
                pageSize: RECIPIENTS_EXPORT_PAGE_SIZE,
                ...(transformedFilters && { filter_array: transformedFilters }),
            };

            const response = await fetchRecipientsList(params);
            return response.recipient_list || [];
        },
        [transformedFilters]
    );

    const handleCSVDataFetched = useCallback((data: Recipient[]) => {
        const filename = `Stan_Recipients_${new Date().toISOString().split('T')[0]}`;
        exportRecipientsToCSV(data, filename);
    }, []);

    const handleSendOptInEmails = useCallback(() => {
        optInModal.open();
    }, [optInModal]);

    const handleOptInSuccess = useCallback(
        (count: number) => {
            setEmailsSent(count);
            successModal.open();
        },
        [successModal]
    );

    const handleSuccessModalClose = useCallback(() => {
        successModal.close();
        // This clears the stale cache and ensures updated data is fetched
        queryClient.removeQueries({
            queryKey: [QUERY_KEYS.RECIPIENTS_LIST],
        });
    }, [successModal, queryClient]);

    return {
        handleExportCSV,
        exportModal,
        fetchPageData,
        handleCSVDataFetched,
        handleSendOptInEmails,
        optInModal,
        successModal,
        emailsSent,
        handleOptInSuccess,
        handleSuccessModalClose,
    };
};
